import {
    Injectable,
    PLATFORM_ID,
    inject,
    signal,
    DestroyRef,
    afterNextRender,
    Injector
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

export interface ScriptLoaderConfig {
    src: string;
    globalName: string;
    attrs?: {
        integrity?: string;
        crossOrigin?: string;
        referrerPolicy?: string;
        nonce?: string;
    };
    timeoutMs?: number;
}

export interface ScriptLoaderState<T> {
    value: T | undefined;
    loading: boolean;
    error: Error | null;
}

// Global caches to prevent duplicate script loading
const scriptPromises = new Map<string, Promise<void>>();
const globalPromises = new Map<string, Promise<any>>();

@Injectable()
export class ScriptLoaderService<T = any> {
    readonly value = signal<T | undefined>(undefined);
    readonly loading = signal<boolean>(false);
    readonly error = signal<Error | null>(null);

    private platformId = inject(PLATFORM_ID);
    private destroyRef = inject(DestroyRef);
    private injector = inject(Injector);
    private destroyed = false;

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.destroyed = true;
        });
    }

    load(config: ScriptLoaderConfig): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if (typeof window !== 'undefined' && (window as any)[config.globalName]) {
            this.value.set((window as any)[config.globalName]);
            this.loading.set(false);
            return;
        }

        afterNextRender(() => {
            this.loadScriptInBrowser(config);
        }, {injector: this.injector});
    }

    reset(config?: {src?: string; globalName?: string}): void {
        this.value.set(undefined);
        this.loading.set(false);
        this.error.set(null);

        if (config?.src) {
            scriptPromises.delete(config.src);

            if (typeof document !== 'undefined') {
                const scriptEl = document.querySelector<HTMLScriptElement>(`script[src="${config.src}"]`);
                if (scriptEl && scriptEl.getAttribute('data-loaded') !== 'true') {
                    scriptEl.remove();
                }
            }
        }

        if (config?.globalName) {
            globalPromises.delete(config.globalName);
        }
    }

    private async loadScriptInBrowser(config: ScriptLoaderConfig): Promise<void> {
        this.loading.set(true);
        this.error.set(null);

        try {
            await this.loadScriptOnce(config.src, config.attrs);

            if (this.destroyed) {
                return;
            }

            const globalValue = await this.waitForGlobal(
                config.globalName,
                config.timeoutMs ?? 15000
            );

            this.value.set(globalValue);
            this.loading.set(false);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            this.error.set(error);
            this.loading.set(false);
        }
    }

    private loadScriptOnce(
        src: string,
        attrs?: ScriptLoaderConfig['attrs']
    ): Promise<void> {
        if (scriptPromises.has(src)) {
            return scriptPromises.get(src)!;
        }

        const promise = new Promise<void>((resolve, reject) => {
            let scriptEl = document.querySelector<HTMLScriptElement>(
                `script[src="${src}"]`
            );

            if (scriptEl && scriptEl.getAttribute('data-loaded') !== 'true') {
                scriptEl.remove();
                scriptEl = null;
            }

            const exists = !!scriptEl;

            if (!scriptEl) {
                scriptEl = document.createElement('script');
                scriptEl.src = src;
                scriptEl.async = true;
                scriptEl.defer = true;

                if (attrs?.integrity) {
                    scriptEl.integrity = attrs.integrity;
                }
                if (attrs?.crossOrigin) {
                    scriptEl.crossOrigin = attrs.crossOrigin;
                }
                if (attrs?.referrerPolicy) {
                    scriptEl.referrerPolicy = attrs.referrerPolicy;
                }
                if (attrs?.nonce) {
                    scriptEl.nonce = attrs.nonce;
                }

                document.head.appendChild(scriptEl);
            }

            if (scriptEl.getAttribute('data-loaded') === 'true') {
                resolve();
                return;
            }

            const onLoad = (): void => {
                scriptEl!.setAttribute('data-loaded', 'true');
                cleanup();
                resolve();
            };

            const onError = (): void => {
                cleanup();
                scriptEl!.remove();
                scriptPromises.delete(src);
                reject(new Error(`Failed to load script: ${src}`));
            };

            const cleanup = (): void => {
                scriptEl!.removeEventListener('load', onLoad);
                scriptEl!.removeEventListener('error', onError);
            };

            scriptEl.addEventListener('load', onLoad);
            scriptEl.addEventListener('error', onError);

            if (exists && scriptEl.getAttribute('data-loaded') === 'true') {
                queueMicrotask(() => {
                    cleanup();
                    resolve();
                });
            }
        });

        scriptPromises.set(src, promise);
        return promise;
    }

    private waitForGlobal(
        globalName: string,
        timeoutMs = 15000
    ): Promise<T> {
        if (globalPromises.has(globalName)) {
            return globalPromises.get(globalName)!;
        }

        const promise = new Promise<T>((resolve, reject) => {
            const startTime = Date.now();
            const intervalMs = 50;

            const check = (): void => {
                if (this.destroyed) {
                    globalPromises.delete(globalName);
                    return;
                }

                if (typeof window !== 'undefined' && (window as any)[globalName]) {
                    resolve((window as any)[globalName]);
                    return;
                }

                if (Date.now() - startTime >= timeoutMs) {
                    globalPromises.delete(globalName);
                    reject(
                        new Error(
                            `Global "${globalName}" not found after ${timeoutMs}ms`
                        )
                    );
                    return;
                }

                setTimeout(check, intervalMs);
            };

            check();
        });

        globalPromises.set(globalName, promise);
        return promise;
    }
}
